/**
 * Held Contingencies Admin Agent — API client for the Admin hub's "Held
 * contingencies" tab: Wisconsin contingencies held for attorney attention and
 * their release (design T17, anvil P2). cutthe6.com doesn't decide legal
 * questions: the admin records what the parties' own documents show.
 *
 * Contract (backend/app/routes/admin_contingency_attention.py):
 *   GET  /api/admin/contingency-attention?reason&state&status&limit&offset
 *        → { items: [{ contingency_id, transaction_id, property_address, state, category,
 *            catalog_key, heading, contract_status, attention_reason,
 *            active_reasons: [{reason, copy_key, short_label}], deadline, deadline_local,
 *            deadline_basis, held_since, deal_status, deal_ended, version, outcomes }],
 *            total, facets: {reasons, states, statuses}, filters, reasons: [{reason, short_label}] }
 *   GET  /api/admin/contingency-attention/{id}
 *        → { contingency: {..., active_reasons, all_reasons, version}, held,
 *            transaction: {id, status, deal_ended, state, property_address, buyer, seller},
 *            outcomes: [{outcome, to_status, evidence_required, needs_deadline, ends_offer}],
 *            refused_outcomes: [{outcome, code}], events, notices, attachments: [{file_id, url, ...}] }
 *   POST /api/admin/contingency-attention/{id}/evidence  (multipart `file`) → { file_id, sha256, ... }
 *   GET  /api/admin/contingency-attention/attachments/{file_id}  → the file
 *   POST /api/admin/contingencies/{id}/resolve
 *        { outcome, note, expected_version, evidence_file_id?, deadline_date?,
 *          year_built_shown?, year_source?, construction_began_1978_or_later? }
 *        (the last three: record_not_applicable, copy §15.1; 422 year_required |
 *        year_source_invalid | year_before_1978 | year_1978_construction_start_unproven)
 *        → { event, deal_ended, notified, detail }
 *        403 admin_required; 409 not_held | outcome_not_allowed | stale_state |
 *        transaction_terminated | not_contract_tracked; 422 note_required |
 *        evidence_required | evidence_invalid | deadline_required | deadline_in_past
 *
 *
 * Copy §15.2, the deal-level record of an offer ended by a document both
 * parties signed (`ADMIN_DEAL_RECORD_ENDED`; available whether or not a row is held):
 *   GET  /api/admin/transactions/{id}/record-ended   (backend/app/services/contingency/deal_record.py)
 *        → { transaction: {id, status, state, category, contract_tracked, deal_ended, offer_id,
 *            property_address, buyer, seller}, version, allowed,
 *            refusal: {code, copy_key, message}|null, open_count, held_count,
 *            rows: [{contingency_id, catalog_key, contract_status, attorney_attention, ...}],
 *            signers: [{person_id, role, name, status}], evidence: [...], copy_keys }
 *        404 transaction_not_found
 *   POST /api/admin/transactions/{id}/record-ended/evidence  (multipart `file`) → { file_id, sha256, ... }
 *   POST /api/admin/transactions/{id}/record-ended
 *        { note, expected_version, evidence_file_id, confirm: true }
 *        → { event, termination_event, transaction_id, ended_at, mooted_contingency_ids, ... }
 *        403 admin_required; 404 transaction_not_found; 409 not_contract_tracked |
 *        already_ended | stale_state; 422 note_required | confirm_required |
 *        version_required | evidence_required | evidence_invalid
 *
 * Errors come from `APIAgent.request` (`.status` and `.detail = {code, message, ...}`).
 */
import APIAgent from './apiAgent';

const BASE = '/api/admin/contingency-attention';
const TXN_BASE = '/api/admin/transactions';

/** `year_source` values for record_not_applicable (copy §15.1 rule 5). */
export const YEAR_SOURCES = ['assessor_record', 'building_permit', 'occupancy_certificate', 'signed_by_both'];

export const heldContingenciesAdminAgent = {
  /**
   * Held rows across deals.
   * @param {{reason?: string, state?: string, status?: string}} [filters]
   */
  list(filters = {}) {
    const params = new URLSearchParams();
    ['reason', 'state', 'status'].forEach((k) => {
      if (filters[k]) params.set(k, filters[k]);
    });
    const qs = params.toString();
    return APIAgent.request(qs ? `${BASE}?${qs}` : BASE, 'GET');
  },

  /** One row with its full history and the outcomes allowed now. */
  get(contingencyId) {
    return APIAgent.request(`${BASE}/${encodeURIComponent(contingencyId)}`, 'GET');
  },

  /**
   * Upload the supporting document (signed amendment or notice).
   * @param {number} contingencyId
   * @param {File} file
   */
  uploadEvidence(contingencyId, file) {
    const form = new FormData();
    form.append('file', file);
    return APIAgent.request(`${BASE}/${encodeURIComponent(contingencyId)}/evidence`, 'POST', form, {}, {
      timeout: 60000,
    });
  },

  /**
   * Record an outcome (audited; a note is always required).
   * @param {number} contingencyId
   * @param {object} args
   * @param {string} args.outcome
   * @param {string} args.note
   * @param {number} args.expectedVersion - the row version the admin saw
   * @param {number} [args.evidenceFileId]
   * @param {string} [args.deadlineDate] - YYYY-MM-DD (Central), set_deadline only
   * @param {number} [args.yearBuiltShown] - record_not_applicable only: the year of original
   *   construction the record shows
   * @param {string} [args.yearSource] - record_not_applicable only: one of YEAR_SOURCES
   * @param {boolean} [args.constructionBegan1978OrLater] - record_not_applicable with 1978 only:
   *   the record also shows construction began on or after January 1, 1978
   */
  resolve(contingencyId, {
    outcome, note, expectedVersion, evidenceFileId, deadlineDate, yearBuiltShown, yearSource,
    constructionBegan1978OrLater,
  }) {
    const body = {
      outcome,
      note: note ? note.trim() : note,
      expected_version: expectedVersion ?? null,
    };
    if (evidenceFileId != null) body.evidence_file_id = evidenceFileId;
    if (deadlineDate) body.deadline_date = deadlineDate;
    if (yearBuiltShown != null && yearBuiltShown !== '') body.year_built_shown = Number(yearBuiltShown);
    if (yearSource) body.year_source = yearSource;
    if (constructionBegan1978OrLater) body.construction_began_1978_or_later = true;
    return APIAgent.request(`/api/admin/contingencies/${encodeURIComponent(contingencyId)}/resolve`, 'POST', body);
  },

  /**
   * What the deal-level "offer ended by a document both parties signed" action
   * needs: the transaction's version and how many rows are still open or held.
   * @param {number} transactionId
   */
  getDealEnd(transactionId) {
    return APIAgent.request(`${TXN_BASE}/${encodeURIComponent(transactionId)}/record-ended`, 'GET');
  },

  /**
   * Upload the document both parties signed (deal-level evidence, copy §15.2).
   * @param {number} transactionId
   * @param {File} file
   */
  uploadDealEvidence(transactionId, file) {
    const form = new FormData();
    form.append('file', file);
    return APIAgent.request(`${TXN_BASE}/${encodeURIComponent(transactionId)}/record-ended/evidence`, 'POST', form, {}, {
      timeout: 60000,
    });
  },

  /**
   * Record that the offer ended by a document both parties signed (audited; ends the deal).
   * Sends `confirm: true`: the dialog only calls this once the admin ticked
   * `WI.admin.deal_end.confirm`.
   * @param {number} transactionId
   * @param {object} args
   * @param {string} args.note
   * @param {number} args.expectedVersion - the preview's `version` the admin saw
   * @param {number} args.evidenceFileId
   */
  recordEnded(transactionId, { note, expectedVersion, evidenceFileId }) {
    return APIAgent.request(`${TXN_BASE}/${encodeURIComponent(transactionId)}/record-ended`, 'POST', {
      note: note ? note.trim() : note,
      expected_version: expectedVersion ?? null,
      evidence_file_id: evidenceFileId ?? null,
      confirm: true,
    });
  },

  /** Download a file on the row (party upload or admin evidence). */
  downloadAttachment(fileId, filename) {
    return APIAgent.download(`${BASE}/attachments/${encodeURIComponent(fileId)}`, filename);
  },
};

export default heldContingenciesAdminAgent;
